import type { SceneGroup } from '@lumen5/luminary';
import type { LexicalEditor } from 'lexical';

import { isReferenceCapture } from './reference-video-transcript';
import { $buildTokenStream } from './transcript-pause-staging';

import { getEditorFromSceneGroupId } from 'js/src/data/transcript/transcript-selectors';
import type { RootState } from 'js/src/libs/RootState';
import { getVoiceDrivenBaseAssetCutRegions } from 'js/src/luminary/luminary-selectors';

/**
 * One token of a scene group's transcript. A pause is just another token, sitting between the
 * words it falls between. Words carry their transcript-time span; a pause carries the span of its
 * silence. This is the single shape every agent-facing transcript view and every word-driven
 * placement tool reads, so what the agent sees is exactly what it matches and edits against.
 */
export interface InterleavedWord {
    word: string;
    startTime: number; // milliseconds
    endTime: number; // milliseconds
    isPause?: boolean; // true for a silence/pause span between spoken words
}

/** The internal word text of a pause token. Rendered to the agent as a `<pause seconds="N"/>` tag. */
const PAUSE_WORD = '[pause]';

/**
 * Insert pause markers into the ordered word list at their correct position. A pause sits
 * between the last word that starts before it and the next word. Words are left untouched;
 * only pause entries are added, so the transcript the agent already knew stays stable.
 */
export function interleavePauses(
    words: InterleavedWord[],
    pauses: { startTime: number; endTime: number }[],
): InterleavedWord[] {
    if (!pauses.length) {
        return words;
    }
    const toPauseWord = (pause: { startTime: number; endTime: number }): InterleavedWord => ({
        word: PAUSE_WORD,
        startTime: pause.startTime,
        endTime: pause.endTime,
        isPause: true,
    });

    const result: InterleavedWord[] = [];
    let pauseIndex = 0;
    for (const word of words) {
        while (pauseIndex < pauses.length && pauses[pauseIndex].startTime < word.startTime) {
            result.push(toPauseWord(pauses[pauseIndex]));
            pauseIndex++;
        }
        result.push(word);
    }
    while (pauseIndex < pauses.length) {
        result.push(toPauseWord(pauses[pauseIndex]));
        pauseIndex++;
    }
    return result;
}

/**
 * Build the interleaved (word + pause) list from a scene group's Lexical editor. Words in a
 * `cutRegions` cut are excluded (see $isInCutRegion); pauses are interleaved among the words by time.
 */
function interleavedTranscriptFromEditor(
    editor: LexicalEditor,
    cutRegions: Array<[number, number]>,
): InterleavedWord[] {
    return editor.getEditorState().read(() =>
        $buildTokenStream(cutRegions).map(
            (token): InterleavedWord =>
                token.kind === 'word'
                    ? {
                          word: token.text,
                          startTime: token.node.startTime,
                          endTime: token.node.endTime,
                      }
                    : {
                          word: PAUSE_WORD,
                          startTime: token.nodes[0].startTime,
                          endTime: token.nodes[token.nodes.length - 1].endTime,
                          isPause: true,
                      },
        ),
    );
}

function persistedWords(
    words: { word: string; startTime: number; endTime: number }[],
): InterleavedWord[] {
    return words.map((w) => ({ word: w.word, startTime: w.startTime, endTime: w.endTime }));
}

/**
 * The interleaved (word + pause) read view of a voice-driven scene group's transcript — what the
 * agent sees via `covers:` (assetSerializer) and what the scene-coverage partition slices.
 *
 * The single source of truth is the scene group's Lexical editor, surfaced by `$buildTokenStream`.
 * This function is one projection of that stream (into `InterleavedWord`s); the word-driven
 * placement/split tools are another, reading the same stream through `$matchBoundaryNodes`. They
 * agree by construction — same editor, same trimmed-out exclusion, same ordering — because they
 * share `$buildTokenStream`, not because the tools call this function.
 *
 * For the video being edited the editor is REQUIRED: it holds the current words with cuts applied
 * (trimmed-out words excluded) plus pauses as SilenceNodes. `TranscriptContentLoader` hydrates one
 * for every scene group, so a group that has a transcript but no editor means hydration didn't
 * happen — we throw rather than silently return cut-blind, pause-less words that would desync from
 * what the tools match against.
 *
 * Two paths legitimately have no editor and fall back to Luminary's persisted `originalTranscript`,
 * cut-blind and pause-less: the storeless structure-only description
 * (`generateVideoDescriptionFromVideoData`), which passes no state at all, and a reference video
 * that ships no saved transcript for the group (see REFERENCE_CAPTURE).
 *
 * Returns [] when the group has no transcript (text-on-media, etc.).
 */
export function getInterleavedTranscript(
    state: RootState | undefined,
    sceneGroup: SceneGroup,
): InterleavedWord[] {
    const transcript = sceneGroup.originalTranscript;
    const transcriptWords = transcript?.transcriptData?.words || transcript?.words || [];
    if (transcriptWords.length === 0) {
        return [];
    }

    // Storeless structure-only description: no state, so no editors — read persisted words directly.
    if (!state) {
        return persistedWords(transcriptWords);
    }

    const editor = getEditorFromSceneGroupId(state, sceneGroup.id);
    if (!editor) {
        // A reference video may ship no saved transcripts, so a missing editor is expected there
        // rather than a hydration bug. See REFERENCE_CAPTURE.
        if (isReferenceCapture(state)) {
            return persistedWords(transcriptWords);
        }
        throw new Error(
            `No Lexical transcript editor for scene group "${sceneGroup.id}" — TranscriptContentLoader should have hydrated one. The agent video context can't be rendered cut-accurately without it.`,
        );
    }
    // Cut-aware via Luminary's cut regions (see $isInCutRegion) so a just-split group reads only its own span.
    const cutRegions = getVoiceDrivenBaseAssetCutRegions(sceneGroup);
    return interleavedTranscriptFromEditor(editor, cutRegions);
}

/**
 * Render one interleaved token as the agent reads and types it: a spoken word verbatim, a pause as
 * a `<pause seconds="N"/>` tag whose N is the silence length in seconds (matching the transcript
 * read view). Kept here so the coverage slice, the transcript view, and the words the agent passes
 * back into placement tools all use one notation.
 */
export function interleavedTokenToString(token: InterleavedWord): string {
    if (token.isPause) {
        const seconds = ((token.endTime - token.startTime) / 1000).toFixed(1);
        return `<pause seconds="${seconds}"/>`;
    }
    return token.word;
}
